import { useState, useEffect } from 'react';
import { useWorkspace } from '../../hooks/useWorkspace';
import { apiClient } from '../../lib/api';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { Card } from '../../components/ui/Card';
import { Modal } from '../../components/ui/Modal';
import { Input } from '../../components/ui/Input';
import { Select } from '../../components/ui/Select';

interface AuditorLink {
  id: string;
  label: string;
  snapshotId: string;
  snapshotDescription: string;
  token: string;
  status: 'active' | 'expired' | 'revoked';
  accessCount: number;
  expiresAt: string;
  createdAt: string;
}

interface SnapshotOption {
  id: string;
  description: string;
}

const EXPIRY_OPTIONS = [
  { value: '7', label: '7 days' },
  { value: '30', label: '30 days' },
  { value: '90', label: '90 days' },
  { value: 'custom', label: 'Custom' },
];

export function AuditorLinkList() {
  const { currentWorkspace } = useWorkspace();
  const [links, setLinks] = useState<AuditorLink[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [snapshots, setSnapshots] = useState<SnapshotOption[]>([]);
  const [revokingId, setRevokingId] = useState<string | null>(null);
  const [copiedId, setCopiedId] = useState<string | null>(null);

  // Create form state
  const [newLabel, setNewLabel] = useState('');
  const [newSnapshotId, setNewSnapshotId] = useState('');
  const [newExpiry, setNewExpiry] = useState('30');
  const [newCustomDays, setNewCustomDays] = useState('');
  const [creating, setCreating] = useState(false);
  const [generatedUrl, setGeneratedUrl] = useState<string | null>(null);

  const plan = currentWorkspace?.plan;
  const isProPlus = plan === 'pro' || plan === 'enterprise';

  useEffect(() => {
    if (!currentWorkspace) return;
    fetchLinks();
  }, [currentWorkspace]);

  async function fetchLinks() {
    if (!currentWorkspace) return;
    setLoading(true);
    setError(null);

    const { data, error: apiError } = await apiClient.get<AuditorLink[]>(
      `/api/tenants/${currentWorkspace.tenantId}/auditor-links`
    );

    if (apiError) {
      setError(apiError.message);
    } else if (data) {
      setLinks(data);
    }
    setLoading(false);
  }

  async function fetchSnapshots() {
    if (!currentWorkspace) return;

    const { data } = await apiClient.get<SnapshotOption[]>(
      `/api/tenants/${currentWorkspace.tenantId}/snapshots`
    );

    if (data) {
      setSnapshots(data);
    }
  }

  function handleOpenCreate() {
    setNewLabel('');
    setNewSnapshotId('');
    setNewExpiry('30');
    setNewCustomDays('');
    setGeneratedUrl(null);
    setError(null);
    fetchSnapshots();
    setShowCreate(true);
  }

  function handleCloseCreate() {
    setShowCreate(false);
    setGeneratedUrl(null);
  }

  async function handleCreateLink() {
    if (!currentWorkspace) return;
    if (!newSnapshotId) {
      setError('Please select a snapshot');
      return;
    }
    if (!newLabel.trim()) {
      setError('Label is required');
      return;
    }

    const expiresInDays =
      newExpiry === 'custom' ? parseInt(newCustomDays, 10) : parseInt(newExpiry, 10);

    if (!expiresInDays || expiresInDays < 1) {
      setError('Please enter a valid expiry duration');
      return;
    }

    setCreating(true);
    setError(null);

    const { data, error: apiError } = await apiClient.post<AuditorLink>(
      `/api/tenants/${currentWorkspace.tenantId}/auditor-links`,
      {
        snapshotId: newSnapshotId,
        label: newLabel.trim(),
        expiresInDays,
      }
    );

    if (apiError) {
      setError(apiError.message);
    } else if (data) {
      setLinks((prev) => [data, ...prev]);
      const url = `${window.location.origin}/auditor/${data.token}`;
      setGeneratedUrl(url);
    }
    setCreating(false);
  }

  async function handleRevoke(linkId: string) {
    if (!currentWorkspace) return;
    setRevokingId(linkId);

    const { error: apiError } = await apiClient.patch(
      `/api/tenants/${currentWorkspace.tenantId}/auditor-links/${linkId}/revoke`
    );

    if (apiError) {
      setError(apiError.message);
    } else {
      setLinks((prev) =>
        prev.map((l) =>
          l.id === linkId ? { ...l, status: 'revoked' as const } : l
        )
      );
    }
    setRevokingId(null);
  }

  async function copyToClipboard(token: string, linkId: string) {
    const url = `${window.location.origin}/auditor/${token}`;
    try {
      await navigator.clipboard.writeText(url);
      setCopiedId(linkId);
      setTimeout(() => setCopiedId(null), 2000);
    } catch {
      // Fallback - do nothing
    }
  }

  function formatDate(dateStr: string) {
    try {
      return new Date(dateStr).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    } catch {
      return dateStr;
    }
  }

  const statusBadgeVariant = (status: AuditorLink['status']) => {
    switch (status) {
      case 'active':
        return 'success' as const;
      case 'expired':
        return 'warning' as const;
      case 'revoked':
        return 'danger' as const;
      default:
        return 'default' as const;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[40vh]">
        <svg className="w-8 h-8 animate-spin text-brand" viewBox="0 0 24 24" fill="none">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
        </svg>
      </div>
    );
  }

  if (!isProPlus) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-display font-bold text-white">
            Auditor Links
          </h1>
          <p className="text-slate-400 text-sm mt-1">
            Share read-only compliance snapshots with auditors
          </p>
        </div>

        <Card>
          <div className="text-center py-12">
            <div className="w-16 h-16 rounded-full bg-brand/10 flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-brand-light" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M10 13a5 5 0 007.54.54l3-3a5 5 0 00-7.07-7.07l-1.72 1.71" />
                <path d="M14 11a5 5 0 00-7.54-.54l-3 3a5 5 0 007.07 7.07l1.71-1.71" />
              </svg>
            </div>
            <h2 className="text-lg font-display font-semibold text-white mb-2">
              Pro+ Feature
            </h2>
            <p className="text-slate-400 text-sm max-w-md mx-auto mb-6">
              Auditor Links are available on Pro and Enterprise plans. Upgrade to share
              read-only compliance snapshots with external auditors.
            </p>
            <Button>Upgrade Plan</Button>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-display font-bold text-white">
            Auditor Links
          </h1>
          <p className="text-slate-400 text-sm mt-1">
            {links.length} link{links.length !== 1 ? 's' : ''}
          </p>
        </div>
        <Button onClick={handleOpenCreate}>Create Link</Button>
      </div>

      {/* Error banner */}
      {error && !showCreate && (
        <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4">
          <p className="text-sm text-red-400">{error}</p>
        </div>
      )}

      {/* Links table */}
      {links.length === 0 ? (
        <Card>
          <div className="text-center py-8">
            <p className="text-slate-400 text-sm">No auditor links created yet.</p>
            <Button
              variant="secondary"
              size="sm"
              className="mt-4"
              onClick={handleOpenCreate}
            >
              Create Your First Link
            </Button>
          </div>
        </Card>
      ) : (
        <div className="bg-surface-mid rounded-xl border border-border overflow-hidden">
          {/* Table header */}
          <div className="hidden lg:grid grid-cols-12 gap-4 px-6 py-3 border-b border-border text-xs text-slate-500 uppercase tracking-wider">
            <div className="col-span-2">Label</div>
            <div className="col-span-3">Snapshot</div>
            <div className="col-span-1">Status</div>
            <div className="col-span-1">Views</div>
            <div className="col-span-2">Expires</div>
            <div className="col-span-3 text-right">Actions</div>
          </div>

          {/* Table rows */}
          {links.map((link) => (
            <div
              key={link.id}
              className="grid grid-cols-1 lg:grid-cols-12 gap-2 lg:gap-4 px-6 py-4 border-b border-border last:border-b-0 items-center"
            >
              <div className="lg:col-span-2">
                <p className="text-sm font-medium text-white truncate">
                  {link.label}
                </p>
              </div>
              <div className="lg:col-span-3">
                <p className="text-sm text-slate-400 truncate">
                  {link.snapshotDescription}
                </p>
              </div>
              <div className="lg:col-span-1">
                <Badge variant={statusBadgeVariant(link.status)}>
                  {link.status}
                </Badge>
              </div>
              <div className="lg:col-span-1">
                <p className="text-sm text-slate-400">{link.accessCount}</p>
              </div>
              <div className="lg:col-span-2">
                <p className="text-sm text-slate-500">
                  {formatDate(link.expiresAt)}
                </p>
              </div>
              <div className="lg:col-span-3 flex items-center justify-end gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => copyToClipboard(link.token, link.id)}
                >
                  {copiedId === link.id ? 'Copied!' : 'Copy URL'}
                </Button>
                {link.status === 'active' && (
                  <Button
                    variant="ghost"
                    size="sm"
                    loading={revokingId === link.id}
                    onClick={() => handleRevoke(link.id)}
                    className="text-red-400 hover:text-red-300"
                  >
                    Revoke
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create Link Modal */}
      <Modal
        isOpen={showCreate}
        onClose={handleCloseCreate}
        title="Create Auditor Link"
        footer={
          generatedUrl ? (
            <Button onClick={handleCloseCreate}>Done</Button>
          ) : (
            <>
              <Button variant="secondary" onClick={handleCloseCreate}>
                Cancel
              </Button>
              <Button loading={creating} onClick={handleCreateLink}>
                Create Link
              </Button>
            </>
          )
        }
      >
        <div className="space-y-4">
          {error && showCreate && (
            <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-3">
              <p className="text-sm text-red-400">{error}</p>
            </div>
          )}

          {generatedUrl ? (
            <div className="space-y-3">
              <p className="text-sm text-slate-300">
                Your auditor link has been created. Share this URL with your auditor:
              </p>
              <div className="flex items-center gap-2 bg-surface-dark border border-border rounded-lg p-3">
                <code className="text-sm text-brand-light flex-1 truncate">
                  {generatedUrl}
                </code>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={async () => {
                    await navigator.clipboard.writeText(generatedUrl);
                    setCopiedId('generated');
                    setTimeout(() => setCopiedId(null), 2000);
                  }}
                >
                  {copiedId === 'generated' ? 'Copied!' : 'Copy'}
                </Button>
              </div>
            </div>
          ) : (
            <>
              <Select
                label="Snapshot"
                placeholder="Select a snapshot"
                options={snapshots.map((s) => ({
                  value: s.id,
                  label: s.description,
                }))}
                value={newSnapshotId}
                onChange={(e) => setNewSnapshotId(e.target.value)}
              />

              <Input
                label="Label"
                placeholder="e.g. External Auditor - Q1 Review"
                value={newLabel}
                onChange={(e) => setNewLabel(e.target.value)}
              />

              <Select
                label="Expires In"
                options={EXPIRY_OPTIONS}
                value={newExpiry}
                onChange={(e) => setNewExpiry(e.target.value)}
              />

              {newExpiry === 'custom' && (
                <Input
                  label="Custom Days"
                  type="number"
                  placeholder="Number of days"
                  value={newCustomDays}
                  onChange={(e) => setNewCustomDays(e.target.value)}
                />
              )}
            </>
          )}
        </div>
      </Modal>
    </div>
  );
}
